/**
 * Holding the derived sync identity between sessions.
 *
 * Deriving it costs 600,000 PBKDF2 iterations, so it is not something to redo on
 * every launch — but it is the material that reaches the account, so it cannot sit
 * on disk in the clear either.
 *
 * It is therefore sealed under the DEK, which means it inherits the keyring's
 * protection exactly: locked app, no identity; PIN removed, no identity; keyring
 * destroyed, the record is unreadable ciphertext. That is deliberate reuse rather
 * than a second mechanism to keep in step with the first.
 *
 * ## What is stored, and what is not
 *
 * The name and passphrase are NOT stored — not sealed, not hashed, nowhere. Only
 * their derived outputs. Someone who compels the device and the PIN gets the
 * account; they do not get the passphrase, which matters because the passphrase is
 * the one secret that would also unlock any OTHER device the person owns.
 */
import type { SyncIdentity } from './syncIdentity'
import { open as openSealed, seal } from './crypto'
import { currentDek } from './keyring'
import { openDb, promisify, STORE, txDone } from './db'

const IDENTITY_KEY = 'syncIdentity'

/**
 * The sealed form.
 *
 * `syncKeyMaterial` becomes base64 because a Uint8Array does not survive the
 * JSON round trip inside the envelope — it would come back as `{"0":12,"1":...}`,
 * an object that looks plausible, indexes like an array, and derives a completely
 * different soul key. Silent, and only visible as a second device that syncs
 * nothing.
 */
interface StoredIdentity {
  v: 1
  alias: string
  seaPassword: string
  syncKeyMaterialB64: string
}

function toB64(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary)
}

function fromB64(value: string): Uint8Array {
  const binary = atob(value)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes
}

export async function saveSyncIdentity(identity: SyncIdentity): Promise<void> {
  const dek = currentDek()
  if (dek === undefined) throw new Error('Cannot store a sync identity while locked')

  const stored: StoredIdentity = {
    v: 1,
    alias: identity.alias,
    seaPassword: identity.seaPassword,
    syncKeyMaterialB64: toB64(identity.syncKeyMaterial),
  }

  /*
   * Sealed BEFORE the transaction opens, not inside it.
   *
   * An IndexedDB transaction stays alive only while requests keep being placed on
   * it; awaiting anything else lets the event loop turn and the transaction
   * auto-closes, so `put` then throws TransactionInactiveError. `seal` is a real
   * await — it is WebCrypto — which makes the inline form fail every time.
   */
  const sealed = await seal(stored, dek)

  const db = await openDb()
  const tx = db.transaction(STORE.meta, 'readwrite')
  tx.objectStore(STORE.meta).put(sealed, IDENTITY_KEY)
  await txDone(tx)
}

/**
 * The stored identity, or undefined.
 *
 * Undefined covers three cases that the caller must treat identically: the app is
 * locked, sync was never enabled, and the record cannot be decrypted because the
 * keyring was replaced. None of them is an error — each simply means there is no
 * identity to sync with right now.
 */
export async function loadSyncIdentity(): Promise<SyncIdentity | undefined> {
  const dek = currentDek()
  if (dek === undefined) return undefined

  const db = await openDb()
  const tx = db.transaction(STORE.meta, 'readonly')
  const sealed = await promisify<unknown>(tx.objectStore(STORE.meta).get(IDENTITY_KEY))
  if (sealed === undefined) return undefined

  try {
    const stored = await openSealed<StoredIdentity>(sealed as never, dek)
    return {
      alias: stored.alias,
      seaPassword: stored.seaPassword,
      syncKeyMaterial: fromB64(stored.syncKeyMaterialB64),
    }
  } catch {
    // Written under a key that no longer exists. Returning undefined degrades to
    // "sync is not set up", which is recoverable by entering the username and
    // passphrase again — throwing here would take down the whole data layer for a
    // record the app can live without.
    return undefined
  }
}

/**
 * Forget the identity.
 *
 * Called when sync is turned off and as part of a full wipe. Turning sync off has
 * to remove this: leaving it would let the app rejoin the account without asking
 * for the passphrase again, which is not what "off" means to the person who
 * pressed it.
 */
export async function clearSyncIdentity(): Promise<void> {
  const db = await openDb()
  const tx = db.transaction(STORE.meta, 'readwrite')
  tx.objectStore(STORE.meta).delete(IDENTITY_KEY)
  await txDone(tx)
}
